import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { invalidateSchedulerConfigsQuery } from '../useSchedulerConfigsQuery';
import { invalidateZoomConnectionQuery } from './useZoomConnectionQuery';

/**
 * Disconnects the current user's (or the impersonated user's) Zoom connection.
 * `DELETE /scheduler/zoom/connection` — best-effort revokes the token with Zoom and clears the stored connection.
 * On success the Zoom connection status query is invalidated so it refetches as `connected: false`, and the scheduler
 * config queries are invalidated too — meeting types saved with `uniqueLinkProvider: 'Zoom'` are now invalid, so
 * cached configs shouldn't keep reporting that provider.
 *
 * Mutations have no MutationCache wired up (see packages/main/CLAUDE.md), so callers MUST pass `onError` to surface
 * failures.
 */
export function useZoomDisconnectMutation({
	onError,
	onSuccess,
}: {
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({ impersonationContext }: { impersonationContext?: Api.IImpersonationContext } = {}) =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'scheduler/zoom/connection',
				}),
				'DELETE'
			),
		onError,
		onSuccess: () => {
			invalidateZoomConnectionQuery();
			invalidateSchedulerConfigsQuery();
			onSuccess?.();
		},
	});
}
